import { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Typography, Fade, ButtonBase, alpha } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCategories } from '../../redux/actions/gameActions';
import { RED } from '../../utils/brandColors';
import Iconify from '../../components/Iconify';

const CATEGORY_ICONS = {
  all: 'eva:grid-outline',
  following: 'eva:people-outline',
  food: 'eva:pie-chart-outline',
  gaming: 'eva:cube-outline',
  sports: 'eva:activity-outline',
  movies: 'eva:film-outline',
  entertainment: 'eva:film-outline',
  entertainmer: 'eva:film-outline',
  music: 'eva:music-outline',
  tech: 'eva:monitor-outline',
  science: 'eva:monitor-outline',
  travel: 'eva:navigation-2-outline',
  lifestyle: 'eva:smiling-face-outline',
  popculture: 'eva:star-outline',
  random: 'eva:shuffle-2-outline',
};

export default function RightCategoriesPanel({ show = false, visibleCategory = null }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    dispatch(fetchCategories()).then((cats) => {
      if (cats) setCategories(cats);
    });
  }, [dispatch]);

  const getSidebarCategories = () => {
    const list = [{ name: 'All', slug: 'all' }];
    if (userInfo && !userInfo.isAnon) {
      list.push({ name: 'Following', slug: 'following' });
    }
    categories.forEach((c) => {
      list.push({ name: c.name, slug: c.slug });
    });
    return list;
  };

  return (
    <Fade in={show} timeout={300} mountOnEnter unmountOnExit>
      <Box
        sx={(theme) => ({
          display: { xs: 'none', lg: 'block' },
          position: 'fixed',
          right: 24,
          top: 110, // Sits perfectly below the top-right header dock pill
          width: 210,
          zIndex: 1000,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(28, 28, 36, 0.92)' : 'rgba(255, 255, 255, 0.93)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
          borderRadius: '20px',
          p: 2.25,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 12px 40px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.05)' 
            : '0 12px 40px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        })}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.disabled',
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 1.5,
            display: 'block',
            mb: 2,
            textTransform: 'uppercase',
          }}
        >
          Categories
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            maxHeight: '320px',
            overflowY: 'auto',
            pr: 0.5,
            // Smooth custom scrollbar
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'divider',
              borderRadius: 3,
              '&:hover': {
                bgcolor: 'text.disabled',
              },
            },
          }}
        >
          {getSidebarCategories().map((cat) => {
            const currentCategory = searchParams.get('category') || 'all';
            const isActive = visibleCategory ? visibleCategory === cat.slug : currentCategory === cat.slug;

            return (
              <ButtonBase
                key={cat.slug}
                component={RouterLink}
                to={cat.slug === 'all' ? '/' : `/?category=${cat.slug}`}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  px: 1.75,
                  py: 1,
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: isActive ? 700 : 500,
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: isActive ? alpha(RED, 0.08) : 'transparent',
                  color: isActive ? RED : 'text.secondary',
                  position: 'relative',
                  overflow: 'hidden',
                  pl: isActive ? 2.25 : 1.75,
                  '&:hover': {
                    bgcolor: isActive ? alpha(RED, 0.12) : 'action.hover',
                    color: isActive ? RED : 'text.primary',
                    pl: 2.25,
                    '& .cat-icon': {
                      color: RED,
                    }
                  },
                  // Vertical active indicator line
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    height: '60%',
                    width: 3,
                    borderRadius: '0 4px 4px 0',
                    bgcolor: RED,
                    transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  }
                }}
              >
                  <Iconify
                    icon={CATEGORY_ICONS[cat.slug.toLowerCase()] || 'eva:hash-outline'}
                    className="cat-icon"
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 1.25,
                      flexShrink: 0,
                      color: isActive ? RED : 'text.disabled',
                      transition: 'all 0.2s ease',
                    }}
                  />
                  <Typography variant="inherit" noWrap>{cat.name}</Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    </Fade>
  );
}
